import { documentsApi } from '../api/modules/documents.api';
import { publicVendorApi } from '../api/modules/publicVendor.api';
import { getApiErrorMessage } from '../api/client';
import { USE_API } from '../config/api';
import { documentFromApi } from '../models';

export async function listDocuments(params = {}) {
  if (!USE_API) return [];
  try {
    const rows = await documentsApi.list(params);
    return (Array.isArray(rows) ? rows : rows?.items || []).map(documentFromApi);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load documents'));
  }
}

export async function presignDocument(body) {
  if (!USE_API) throw new Error('Document upload requires API mode');
  try {
    return await documentsApi.presign(body);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to get upload URL'));
  }
}

export async function confirmDocument(id) {
  if (!USE_API) throw new Error('Document confirm requires API mode');
  try {
    return documentFromApi(await documentsApi.confirm({ documentId: id }));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to confirm document'));
  }
}

export async function deleteDocument(id) {
  if (!USE_API) throw new Error('Document delete requires API mode');
  try {
    await documentsApi.remove(id);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to delete document'));
  }
}

export async function swapAadhaarDocuments({ ownerType, ownerId }) {
  if (!USE_API) throw new Error('Aadhaar swap requires API mode');
  try {
    return await documentsApi.swapAadhaar({ ownerType, ownerId: Number(ownerId) });
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to swap Aadhaar sides'));
  }
}

/** Full 3-step upload: presign → PUT to S3 → confirm */
export async function uploadDocument({ ownerType, ownerId, docType, file }) {
  if (!USE_API) throw new Error('Document upload requires API mode');
  if (!file) throw new Error('Choose a file to upload');
  if (!ownerId) throw new Error('Missing owner id');
  if (!docType) throw new Error('Missing document type');
  try {
    const mimeType =
      file.type ||
      (/\.pdf$/i.test(file.name || '') ? 'application/pdf' : 'image/jpeg');
    const presign = await documentsApi.presign({
      ownerType,
      ownerId,
      docType,
      fileName: file.name || `${docType}.jpg`,
      mimeType,
      sizeBytes: file.size,
    });
    const uploadUrl = presign.uploadUrl || presign.presignedUrl || presign.url;
    const documentId = presign.documentId || presign.id;
    if (!uploadUrl || !documentId) {
      throw new Error('Invalid presign response');
    }
    const putRes = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': mimeType },
      body: file,
    });
    if (!putRes.ok) {
      throw new Error('S3 upload failed');
    }
    return documentFromApi(await documentsApi.confirm({ documentId }));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, error?.message || 'Failed to upload document'));
  }
}

/**
 * Upload/replace a member equipment document (owner_type = user_equipment).
 * Removes older rows of the same docType so only the latest remains.
 */
export async function uploadUserEquipmentDocument({ equipmentId, docType, file }) {
  const uploaded = await uploadDocument({
    ownerType: 'user_equipment',
    ownerId: Number(equipmentId),
    docType,
    file,
  });
  try {
    const existing = await listDocuments({
      ownerType: 'user_equipment',
      ownerId: Number(equipmentId),
    });
    await Promise.all(
      (existing || [])
        .filter((d) => d && d.docType === docType && d.id !== uploaded.id)
        .map((d) => deleteDocument(d.id)),
    );
  } catch {
    // Non-fatal — new file is already confirmed.
  }
  return uploaded;
}

/**
 * Public vendor registration upload (uses short-lived uploadToken from /register).
 */
export async function uploadPublicVendorDocument({ uploadToken, docType, file }) {
  if (!USE_API) throw new Error('Document upload requires API mode');
  if (!uploadToken) throw new Error('Missing upload token');
  if (!file) throw new Error('Missing file');
  try {
    const presign = await publicVendorApi.documentPresign({
      uploadToken,
      docType,
      fileName: file.name,
      mimeType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
    });
    const uploadUrl = presign.uploadUrl || presign.presignedUrl || presign.url;
    const documentId = presign.documentId || presign.id;
    if (!uploadUrl || !documentId) {
      throw new Error('Invalid presign response');
    }
    const putRes = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      body: file,
    });
    if (!putRes.ok) {
      throw new Error('S3 upload failed');
    }
    return documentFromApi(
      await publicVendorApi.documentConfirm({ uploadToken, documentId }),
    );
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to upload document'));
  }
}
